import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { RouterProvider } from 'react-router';

import { AppErrorBoundary } from '@/components/app-error-boundary';
import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import i18n, { initI18n, isLocale, LOCALE_STORAGE_KEY } from '@/i18n';
import type { Locale } from '@/i18n';
import { ApiRequestError } from '@/lib/api';
import { configureNumerals } from '@/lib/formatters';
import { router } from '@/routes';
import { initialiseAppearance } from '@/stores/appearance';
import { useAuthStore } from '@/stores/auth';
import type { Bootstrap } from '@/types';

import '../css/app.css';

declare global {
    interface Window {
        __DB_CX__?: Bootstrap;
    }
}

/**
 * Resolve the starting locale.
 *
 * The stored preference wins over the server's default, because the server
 * renders the shell before it knows who is looking at it — a kiosk in Salalah
 * that was switched to Arabic must come back up in Arabic.
 */
function resolveInitialLocale(bootstrap: Bootstrap | undefined): Locale {
    try {
        const stored = localStorage.getItem(LOCALE_STORAGE_KEY);

        if (isLocale(stored)) {
            return stored;
        }
    } catch {
        // Storage can be unavailable in a locked-down kiosk profile or private
        // browsing; the server default is a perfectly good answer.
    }

    return bootstrap?.locale ?? 'ar';
}

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            /*
             * A minute of freshness. Long enough that navigating between screens
             * does not re-fetch the same customer list repeatedly; short enough
             * that a complaints officer working a queue sees other people's
             * changes without hunting for a refresh button. Screens with genuinely
             * live data (the wallboard, queue displays) use websockets rather than
             * a shorter interval.
             */
            staleTime: 60_000,
            gcTime: 5 * 60_000,
            refetchOnWindowFocus: false,
            retry: (failureCount, error) => {
                // Never retry a request the server refused on purpose — a 403 will
                // be refused identically three more times, and retrying a 422
                // would re-submit a form the user needs to correct.
                if (error instanceof ApiRequestError && !error.isRetryable) {
                    return false;
                }

                return failureCount < 2;
            },
        },
        mutations: {
            retry: false,
        },
    },
});

function boot(): void {
    const bootstrap = window.__DB_CX__;
    const locale = resolveInitialLocale(bootstrap);

    initI18n(locale);

    if (bootstrap) {
        useAuthStore.getState().initialise({ ...bootstrap, locale });

        configureNumerals(
            Boolean(bootstrap.settings['localisation.arabic_indic_digits']),
        );
    }

    initialiseAppearance();

    const container = document.getElementById('app');

    if (!container) {
        throw new Error('The #app mount point is missing from the shell.');
    }

    createRoot(container).render(
        <StrictMode>
            <AppErrorBoundary>
                <I18nextProvider i18n={i18n}>
                    <QueryClientProvider client={queryClient}>
                        <TooltipProvider delayDuration={200}>
                            <RouterProvider router={router} />
                            <Toaster
                                position="top-center"
                                closeButton
                                richColors
                            />
                        </TooltipProvider>
                    </QueryClientProvider>
                </I18nextProvider>
            </AppErrorBoundary>
        </StrictMode>,
    );

    // Remove the pre-mount spinner from the Blade shell now that React has
    // painted, rather than hiding it with CSS — leaving it in the tree would keep
    // its aria-live region announcing "Loading" to screen readers forever.
    document.getElementById('dbcx-boot')?.remove();
}

boot();
